import { useMutation, useQueryClient } from '@tanstack/react-query';
import { appointmentsApi } from '@/lib/api/appointments';
import { getLocalTimezone, QUERY_KEYS } from '@/lib';
import { formatTimeFor12Hour } from '@/lib/utils/timeUtils';

interface ReschedulePayload {
    bookingId: string;
    date: string;
    startTime: string;
    endTime: string;
    reason?: string;
}

/**
 * Mutation hook for rescheduling an appointment
 */
export function useRescheduleAppointment() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ bookingId, date, startTime, endTime, reason }: ReschedulePayload) => {
            const payload = {
                timezone: getLocalTimezone(),
                date,
                start: formatTimeFor12Hour(date, startTime),
                end: formatTimeFor12Hour(date, endTime),
                reschedule_reason: reason || '', // Always include, even if empty
            };

            await appointmentsApi.rescheduleAppointment(bookingId, payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BOOKINGS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BLOCKED_TIMES] });
        },
    });
}

/**
 * Mutation hook for canceling an appointment
 */
export function useCancelAppointment() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ bookingId, userId }: { bookingId: string; userId?: string | number }) => {
            await appointmentsApi.cancelAppointment(bookingId, {
                timezone: getLocalTimezone(),
                reason: `Cancelled from admin by user_id:${userId || 'undefined'}`,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BOOKINGS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BLOCKED_TIMES] });
        },
    });
}
